import React, { type CSSProperties, type ErrorInfo, type ReactNode } from 'react';
import * as storage from '../lib/storage.js';

interface Props {
  children: ReactNode;
  reloadApp?: () => void;
}

interface State {
  hasError: boolean;
}

export class AppErrorBoundary extends React.Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Truebend startup error', error, errorInfo);
  }

  override render() {
    if (!this.state.hasError) return this.props.children;
    const reloadApp = this.props.reloadApp ?? reloadPage;

    return (
      <main style={shellStyle}>
        <section aria-label="Truebend startup recovery" style={panelStyle}>
          <div style={eyebrowStyle}>Truebend</div>
          <h1 style={titleStyle}>Truebend needs a restart</h1>
          <p style={copyStyle}>
            Close and reopen the app to reload the calculators. If it keeps happening, reset saved
            app data and start fresh.
          </p>
          <div style={buttonGroupStyle}>
            <button type="button" style={buttonStyle} onClick={reloadApp}>
              Reload app
            </button>
            <button
              type="button"
              style={secondaryButtonStyle}
              onClick={() => resetSavedAppDataAndReload(reloadApp)}
            >
              Reset saved app data
            </button>
          </div>
        </section>
      </main>
    );
  }
}

export function resetSavedAppDataAndReload(reloadApp: () => void = reloadPage): void {
  try {
    storage.clearAll();
  } catch (error) {
    console.error('Truebend saved data reset failed', error);
  }
  reloadApp();
}

function reloadPage(): void {
  window.location.reload();
}

const shellStyle: CSSProperties = {
  minHeight: '100dvh',
  display: 'grid',
  placeItems: 'center',
  padding: 'var(--space-4)',
  background: 'var(--color-bg)',
};

const panelStyle: CSSProperties = {
  width: 'min(100%, 430px)',
  display: 'grid',
  gap: 'var(--space-3)',
  padding: 'var(--space-5)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-card)',
};

const eyebrowStyle: CSSProperties = {
  color: 'var(--color-accent)',
  fontSize: 'var(--text-xs)',
  fontWeight: 900,
  textTransform: 'uppercase',
};

const titleStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-2xl)',
  lineHeight: 1.08,
  margin: 0,
};

const copyStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-base)',
  margin: 0,
};

const buttonStyle: CSSProperties = {
  minHeight: 48,
  borderRadius: 'var(--radius-md)',
  color: 'var(--color-text-on-orange)',
  background: 'var(--color-accent)',
  border: '1px solid var(--color-accent)',
  fontWeight: 900,
};

const buttonGroupStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-2)',
};

const secondaryButtonStyle: CSSProperties = {
  ...buttonStyle,
  color: 'var(--color-text-primary)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
};
